import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { updateDealSchema, type DealResponse, type UpdateDealInput } from '@orion/contracts';
import { Avatar, Button, Card, Input, Menu } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { Timeline } from '../activities/Timeline.js';
import { LogActivityComposer } from '../activities/LogActivityComposer.js';
import { NotesPanel } from '../notes/NotesPanel.js';
import { AttachmentsPanel } from '../attachments/AttachmentsPanel.js';
import { OpenTasksCard } from '../tasks/OpenTasksCard.js';
import { useTasksQuery, useUpdateTask } from '../tasks/api.js';
import { useContactsQuery } from '../contacts/api.js';
import {
  useAddDealLineItem,
  useAttachDealContact,
  useDealContactsQuery,
  useDealLineItemsQuery,
  useDealQuery,
  useDealStageHistoryQuery,
  useLossReasonsQuery,
  useMoveDealStage,
  usePipelinesQuery,
  useUpdateDeal,
} from './api.js';

type Tab = 'overview' | 'activity' | 'products' | 'tasks' | 'notes' | 'files';

function money(value: string | number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(value));
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days} d`;
  if (hours > 0) return `${hours} h ${minutes} m`;
  return `${minutes} m`;
}

/** docs section 13 "Opportunities / Deals" — the single-deal view. Matches
 * design/orion-crm-design/rendered-html/DealDetail.html's layout: a chevron
 * stage stepper (open pipeline stages collapsed to one trailing "Closed"
 * segment once won/lost, rather than separate Won/Lost segments while a
 * deal could still go either way), 6 tabs, and a 3-column Overview.
 *
 * No "Hot" badge — Opportunity has no Lead-style temperature field to back
 * one. No campaign line under Source — LeadAttribution.campaign exists but
 * isn't threaded through the conversion path yet, so only the real source
 * name is shown. No "avg. time in stage for won deals" stat — it needs a
 * cross-deal aggregate this pass didn't build. */
export function DealDetailPage() {
  const { dealId } = useParams({ from: '/_app/deals/$dealId' });
  const { data: deal, isLoading, isError } = useDealQuery(dealId);
  const { data: pipelines } = usePipelinesQuery();
  const { data: lossReasons } = useLossReasonsQuery();
  const { data: stageHistory } = useDealStageHistoryQuery(dealId);
  const { data: dealContacts } = useDealContactsQuery(dealId);
  const { data: lineItems } = useDealLineItemsQuery(dealId);
  const { data: nextTasks } = useTasksQuery({ opportunityId: dealId, status: 'open', pageSize: 1 });
  const moveStage = useMoveDealStage();
  const updateTask = useUpdateTask();
  const [error, setError] = useState<string | null>(null);
  const [showLossPicker, setShowLossPicker] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [tab, setTab] = useState<Tab>('overview');

  if (isLoading) return <PageHeader breadcrumb={[{ label: 'Opportunities', to: '/deals' }]} />;
  if (isError || !deal) {
    return (
      <>
        <PageHeader breadcrumb={[{ label: 'Opportunities', to: '/deals' }]} />
        <main className="p-7 text-sm text-text-muted">We couldn't find that opportunity, or you don't have access to it.</main>
      </>
    );
  }

  const pipeline = pipelines?.find((p) => p.id === deal.pipeline.id);
  const openStages = pipeline ? [...pipeline.stages].filter((s) => s.type === 'open').sort((a, b) => a.position - b.position) : [];
  const lostStage = pipeline?.stages.find((s) => s.type === 'lost');
  const wonStage = pipeline?.stages.find((s) => s.type === 'won');
  const isClosed = deal.stage.type !== 'open';
  const currentPosition = openStages.find((s) => s.id === deal.stage.id)?.position ?? Infinity;
  const nextTask = nextTasks?.data[0];

  const move = async (stageId: string, lossReasonId?: string) => {
    setError(null);
    try {
      await moveStage.mutateAsync({ id: deal.id, input: { stageId, lossReasonId } });
      setShowLossPicker(false);
    } catch {
      setError('Could not update this opportunity. Please try again.');
    }
  };

  const stageDwellLabel = (() => {
    const days = daysBetween(new Date(deal.stageEnteredAt), new Date());
    return days === 0 ? 'today' : `${days}d`;
  })();

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Opportunities', to: '/deals' }, { label: deal.name }]} />
      <main className="flex flex-1 flex-col overflow-hidden px-7 pb-6 pt-5">
        {error && (
          <div role="alert" className="mb-3 shrink-0 rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        {showEdit && <EditDealModal deal={deal} onClose={() => setShowEdit(false)} />}

        {showLossPicker && lostStage && (
          <Card className="mb-3 flex shrink-0 items-center gap-3 p-4">
            <span className="text-sm font-medium">Why was this opportunity lost?</span>
            <select
              className="h-9 max-w-xs flex-1 rounded-control border border-border-strong bg-surface px-2 text-sm"
              defaultValue=""
              onChange={(e) => e.target.value && move(lostStage.id, e.target.value)}
            >
              <option value="" disabled>
                Select a reason…
              </option>
              {(lossReasons ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <Button size="sm" variant="secondary" onClick={() => setShowLossPicker(false)}>
              Cancel
            </Button>
          </Card>
        )}

        <div className="flex shrink-0 items-start gap-4">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary-soft">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="7" width="18" height="13" rx="2" />
              <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
              <path d="M3 13h18" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-3">
              <h1 className="text-[22px] font-[650] leading-none tracking-[-0.01em] text-ink">{deal.name}</h1>
              <span className="text-[22px] font-[650] leading-none tabular-nums tracking-[-0.02em] text-ink">{money(deal.amount, deal.currency)}</span>
              <span className="text-[13px] font-semibold text-text-muted">{deal.currency}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-text-secondary">
              {deal.company && (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="4" y="3" width="12" height="18" rx="1" />
                    <path d="M16 9h4v12h-4" />
                    <path d="M8 7h4M8 11h4M8 15h4" />
                  </svg>
                  <span className="font-semibold">{deal.company.name}</span>
                </>
              )}
              <span className="text-border-strong">·</span> Probability <b className="font-semibold text-ink">{deal.stage.probability}%</b>
              {deal.expectedCloseDate && (
                <>
                  <span className="text-border-strong">·</span> Expected close <b className="font-semibold text-ink">{formatDate(deal.expectedCloseDate)}</b>{' '}
                  <span className="text-text-muted">({daysBetween(new Date(), new Date(deal.expectedCloseDate)) >= 0 ? `in ${daysBetween(new Date(), new Date(deal.expectedCloseDate))} days` : 'overdue'})</span>
                </>
              )}
              {deal.owner && (
                <>
                  <span className="text-border-strong">·</span>
                  <Avatar name={deal.owner.displayName} size="xs" />
                  {deal.owner.displayName}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" aria-label="Edit opportunity" onClick={() => setShowEdit(true)} className="flex size-9 items-center justify-center rounded-control border border-border-strong bg-surface hover:bg-surface-subtle">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 20h4L19 9l-4-4L4 16z" />
                <path d="m13 7 4 4" />
              </svg>
            </button>
            <Menu
              trigger={
                <button type="button" aria-label="More actions" className="flex size-9 items-center justify-center rounded-control hover:bg-surface-subtle">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                    <circle cx="5" cy="12" r="0.8" />
                    <circle cx="12" cy="12" r="0.8" />
                    <circle cx="19" cy="12" r="0.8" />
                  </svg>
                </button>
              }
              items={[
                { label: 'Open pipeline board', onSelect: () => window.location.assign('/deals') },
                { label: 'Copy link', onSelect: () => void navigator.clipboard?.writeText(window.location.href) },
              ]}
            />
          </div>
        </div>

        <div className="mt-4 flex shrink-0 items-center gap-3 rounded-card border border-border bg-surface p-2 shadow-card">
          <ol aria-label="Opportunity stage" className="flex flex-1 gap-1">
            {openStages.map((stage, i) => {
              const done = isClosed || stage.position < currentPosition;
              const current = !isClosed && stage.id === deal.stage.id;
              return (
                <li
                  key={stage.id}
                  aria-current={current ? 'step' : undefined}
                  className="flex h-9 flex-1 items-center justify-center gap-1.5 text-[13px] font-semibold"
                  style={{
                    marginLeft: i > 0 ? '-8px' : undefined,
                    background: current ? 'var(--color-primary)' : done ? 'var(--color-success-soft)' : 'var(--color-divider)',
                    color: current ? '#FFFFFF' : done ? 'var(--color-success)' : 'var(--color-text-muted)',
                    fontWeight: current ? 650 : 600,
                    clipPath:
                      i === 0
                        ? 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)'
                        : 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)',
                    borderRadius: i === 0 ? '8px 0 0 8px' : undefined,
                  }}
                >
                  {done && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="m5 12 5 5 9-10" />
                    </svg>
                  )}
                  {stage.name}
                  {current && <span className="font-medium opacity-80">· {stageDwellLabel}</span>}
                </li>
              );
            })}
            <li
              className="flex h-9 flex-1 items-center justify-center gap-1.5 text-[13px] font-semibold"
              style={{
                marginLeft: '-8px',
                background: deal.stage.type === 'won' ? 'var(--color-success-soft)' : deal.stage.type === 'lost' ? 'var(--color-danger-soft)' : 'var(--color-divider)',
                color: deal.stage.type === 'won' ? 'var(--color-success)' : deal.stage.type === 'lost' ? 'var(--color-danger)' : 'var(--color-text-muted)',
                clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 12px 50%)',
                borderRadius: '0 8px 8px 0',
              }}
            >
              {isClosed && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {deal.stage.type === 'won' ? <path d="m5 12 5 5 9-10" /> : <path d="M6 6l12 12M18 6 6 18" />}
                </svg>
              )}
              {isClosed ? `Closed · ${deal.stage.type === 'won' ? 'Won' : 'Lost'}` : 'Closed'}
            </li>
          </ol>
          {!isClosed && (
            <div className="flex shrink-0 gap-2">
              {wonStage && (
                <Button size="sm" onClick={() => move(wonStage.id)} loading={moveStage.isPending} className="bg-success hover:brightness-95">
                  Mark won
                </Button>
              )}
              {lostStage && (
                <Button size="sm" variant="secondary" onClick={() => setShowLossPicker(true)} loading={moveStage.isPending}>
                  Mark lost
                </Button>
              )}
            </div>
          )}
        </div>

        <div role="tablist" aria-label="Opportunity sections" className="mt-2 flex h-11 shrink-0 items-stretch gap-6 border-b border-border">
          {(
            [
              ['overview', 'Overview'],
              ['activity', 'Activity'],
              ['products', 'Products', lineItems?.length],
              ['tasks', 'Tasks'],
              ['notes', 'Notes'],
              ['files', 'Files'],
            ] as const
          ).map(([key, label, badge]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={tab === key ? 'flex items-center gap-1.5 text-sm font-semibold text-ink shadow-[inset_0_-2px_0_var(--color-primary)]' : 'flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-ink'}
            >
              {label}
              {badge !== undefined && badge > 0 && <span className="rounded-full bg-neutral-pill-soft px-1.75 py-px text-xs font-semibold text-text-secondary">{badge}</span>}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="mt-4 grid min-h-0 flex-1 grid-cols-[328px_1fr_300px] gap-4 overflow-hidden">
            <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
              <Card className="flex flex-col gap-2.5 p-4">
                <h2 className="text-[15px] font-semibold text-ink">Opportunity details</h2>
                <dl className="grid grid-cols-[104px_1fr] gap-x-2 gap-y-2 text-[13px] leading-4.5">
                  <dt className="text-text-muted">Pipeline</dt>
                  <dd>{deal.pipeline.name}</dd>
                  <dt className="text-text-muted">Stage</dt>
                  <dd className="font-semibold">
                    {deal.stage.name} · {deal.stage.probability}%
                  </dd>
                  <dt className="text-text-muted">Status</dt>
                  <dd className={`capitalize ${deal.status === 'abandoned' ? 'font-semibold text-text-muted' : ''}`}>{deal.status}</dd>
                  <dt className="text-text-muted">Source</dt>
                  <dd>{deal.source ?? deal.createdFromLead?.sourceName ?? '—'}</dd>
                  <dt className="text-text-muted">Primary contact</dt>
                  <dd>{deal.primaryContact ? <a className="font-semibold hover:underline">{deal.primaryContact.displayName}</a> : '—'}</dd>
                  <dt className="text-text-muted">Created</dt>
                  <dd className="flex flex-col gap-1">
                    <span>
                      {formatDate(deal.createdAt)}
                      {deal.createdFromLead && ' from lead conversion'}
                    </span>
                    {deal.createdFromLead && (
                      <a href={`/leads/${deal.createdFromLead.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-soft-text hover:underline">
                        View original lead
                      </a>
                    )}
                  </dd>
                </dl>
              </Card>

              <ProductsCard dealId={deal.id} lineItems={lineItems} />
            </div>

            <div className="flex min-h-0 flex-col gap-3">
              <LogActivityComposer opportunityId={deal.id} />
              <Card className="min-h-0 flex-1 overflow-y-auto p-4">
                <Timeline opportunityId={deal.id} />
              </Card>
            </div>

            <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
              {nextTask && (
                <section className="flex flex-col gap-2 rounded-card p-3.5" style={{ background: 'var(--color-amber-soft-2)' }}>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--color-amber-text)' }}>
                    Next step
                  </span>
                  <div className="flex items-start gap-2.5">
                    <button
                      type="button"
                      aria-label="Mark complete"
                      onClick={() => updateTask.mutate({ id: nextTask.id, input: { status: 'completed', version: nextTask.version } })}
                      className="mt-0.5 size-4.5 shrink-0 rounded-full border-[1.5px] bg-surface"
                      style={{ borderColor: 'var(--color-amber-deep)' }}
                    />
                    <div className="flex flex-col gap-0.5 text-[13px]">
                      <span className="text-sm font-semibold text-ink">{nextTask.title}</span>
                      <span style={{ color: 'var(--color-amber-text)' }}>
                        {nextTask.dueAt ? formatDate(nextTask.dueAt) : 'No due date'}
                        {nextTask.owner ? ` · ${nextTask.owner.displayName}` : ''}
                      </span>
                    </div>
                  </div>
                </section>
              )}

              {stageHistory && stageHistory.length > 0 && (
                <Card className="flex flex-col gap-2.5 p-4">
                  <h2 className="text-[15px] font-semibold text-ink">Stage history</h2>
                  <div className="flex flex-col gap-2 text-[13px]">
                    {stageHistory.map((h) => (
                      <div key={`${h.stageId}-${h.enteredAt}`} className="grid grid-cols-[12px_1fr_auto] items-center gap-2">
                        <span className="size-2 rounded-full" style={{ background: h.isCurrent ? 'var(--color-primary)' : 'var(--color-success)' }} />
                        <span>
                          <b className="font-semibold">{h.stageName}</b> <span className="text-text-muted">· {formatDate(h.enteredAt)}</span>
                        </span>
                        <span className={h.isCurrent ? 'font-semibold text-primary-soft-text' : 'tabular-nums text-text-muted'}>{h.isCurrent ? 'Current' : formatDuration(h.durationSeconds)}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              <ContactsOnDealCard dealId={deal.id} contacts={dealContacts} />
            </div>
          </div>
        )}

        {tab === 'activity' && (
          <Card className="mt-4 min-h-0 flex-1 overflow-y-auto p-4">
            <Timeline opportunityId={deal.id} />
          </Card>
        )}
        {tab === 'products' && (
          <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
            <ProductsCard dealId={deal.id} lineItems={lineItems} />
          </div>
        )}
        {tab === 'tasks' && (
          <Card className="mt-4 min-h-0 flex-1 overflow-y-auto p-4">
            <OpenTasksCard opportunityId={deal.id} bare />
          </Card>
        )}
        {tab === 'notes' && (
          <Card className="mt-4 min-h-0 flex-1 overflow-y-auto p-4">
            <NotesPanel opportunityId={deal.id} />
          </Card>
        )}
        {tab === 'files' && (
          <Card className="mt-4 min-h-0 flex-1 overflow-y-auto p-4">
            <AttachmentsPanel opportunityId={deal.id} />
          </Card>
        )}
      </main>
    </>
  );
}

function ProductsCard({ dealId, lineItems }: { dealId: string; lineItems: { id: string; name: string; quantity: string; unitPrice: string; discount: string; total: string }[] | undefined }) {
  const addLineItem = useAddDealLineItem(dealId);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');

  const total = (lineItems ?? []).reduce((sum, li) => sum + Number(li.total), 0);

  const submit = async () => {
    if (!name.trim() || !unitPrice) return;
    await addLineItem.mutateAsync({ name: name.trim(), quantity: Number(quantity) || 1, unitPrice: Number(unitPrice), discount: 0 });
    setName('');
    setQuantity('1');
    setUnitPrice('');
    setAdding(false);
  };

  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 pb-2.5 pt-3.5">
        <h2 className="text-[15px] font-semibold text-ink">Products &amp; services</h2>
        <button type="button" onClick={() => setAdding((a) => !a)} className="flex items-center gap-1 text-[13px] font-semibold text-primary-soft-text">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-soft-text)" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add
        </button>
      </div>
      {(lineItems ?? []).length === 0 && !adding ? (
        <p className="px-4 pb-4 text-sm text-text-muted">No products added yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-[1fr_76px_76px] items-center border-y border-border bg-surface-subtle px-4 py-1.5 text-xs font-semibold text-text-muted">
            <span>Item</span>
            <span className="text-right">Qty × price</span>
            <span className="text-right">Total</span>
          </div>
          {(lineItems ?? []).map((li) => (
            <div key={li.id} className="grid grid-cols-[1fr_76px_76px] items-center border-b border-divider px-4 py-2 text-[13px] tabular-nums">
              <span className="font-semibold text-ink">{li.name}</span>
              <span className="text-right text-xs text-text-secondary">
                {Number(li.quantity)} × {money(li.unitPrice)}
              </span>
              <span className="text-right font-semibold">{money(li.total)}</span>
            </div>
          ))}
          {total > 0 && (
            <div className="flex justify-between bg-surface-subtle px-4 py-2.5 text-sm font-[650] tabular-nums">
              <span>Total</span>
              <span>{money(total)}</span>
            </div>
          )}
        </>
      )}
      {adding && (
        <div className="flex items-center gap-2 border-t border-border p-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Item name" className="h-8 flex-1 rounded-control border border-border-strong px-2 text-sm" />
          <input value={quantity} onChange={(e) => setQuantity(e.target.value)} type="number" placeholder="Qty" className="h-8 w-16 rounded-control border border-border-strong px-2 text-sm" />
          <input value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} type="number" placeholder="Price" className="h-8 w-24 rounded-control border border-border-strong px-2 text-sm" />
          <Button size="sm" onClick={submit} loading={addLineItem.isPending}>
            Save
          </Button>
        </div>
      )}
    </Card>
  );
}

function ContactsOnDealCard({ dealId, contacts }: { dealId: string; contacts: { contactId: string; displayName: string; jobTitle: string | null; role: string | null; isPrimary: boolean }[] | undefined }) {
  const attach = useAttachDealContact(dealId);
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const { data: matches } = useContactsQuery({ q, pageSize: 5 });

  return (
    <Card className="flex flex-col gap-2.5 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[15px] font-semibold text-ink">Contacts on opportunity</h2>
        <button type="button" aria-label="Add contact to opportunity" onClick={() => setSearching((s) => !s)} className="flex size-7 items-center justify-center rounded-chip hover:bg-surface-subtle">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      </div>
      {searching && (
        <div className="flex flex-col gap-1.5">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts…" className="h-8 rounded-control border border-border-strong px-2 text-sm" />
          {q && (matches?.data.length ?? 0) > 0 && (
            <div className="flex flex-col gap-1">
              {matches!.data.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={async () => {
                    await attach.mutateAsync({ contactId: c.id, isPrimary: false });
                    setQ('');
                    setSearching(false);
                  }}
                  className="rounded-control px-2 py-1 text-left text-[13px] hover:bg-surface-subtle"
                >
                  {c.fullName}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {(contacts ?? []).length === 0 && !searching && <p className="text-sm text-text-muted">No contacts attached yet.</p>}
      {(contacts ?? []).map((c) => (
        <div key={c.contactId} className="flex items-center gap-2.5">
          <Avatar name={c.displayName} size="sm" />
          <div className="flex flex-1 flex-col leading-tight">
            <a className="text-[13px] font-semibold text-ink hover:underline">{c.displayName}</a>
            {c.jobTitle && <span className="text-xs text-text-muted">{c.jobTitle}</span>}
          </div>
          {c.role && <span className="rounded-[6px] bg-violet-soft px-2 py-0.5 text-xs font-semibold" style={{ color: 'var(--color-violet)' }}>{c.role}</span>}
        </div>
      ))}
    </Card>
  );
}

function EditDealModal({
  deal,
  onClose,
}: {
  deal: { id: string; name: string; amount: string; expectedCloseDate: string | null; source: string | null; status: DealResponse['status']; version: number };
  onClose: () => void;
}) {
  const updateDeal = useUpdateDeal(deal.id);
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<UpdateDealInput>({
    resolver: zodResolver(updateDealSchema),
    defaultValues: {
      name: deal.name,
      amount: Number(deal.amount),
      expectedCloseDate: deal.expectedCloseDate ?? undefined,
      source: deal.source ?? undefined,
      status: deal.status === 'open' || deal.status === 'abandoned' ? deal.status : undefined,
      version: deal.version,
    },
  });

  const onSubmit = async (input: UpdateDealInput) => {
    await updateDeal.mutateAsync(input);
    onClose();
  };

  // won/lost stay exclusively moveStage()'s job (DealsService#update
  // rejects a status change either way) — the dropdown only ever offers
  // open/abandoned, and is hidden entirely once a deal has actually closed.
  const statusEditable = deal.status === 'open' || deal.status === 'abandoned';

  return (
    <>
      <div className="fixed inset-0 z-40 bg-night/42" onClick={onClose} />
      <Card className="fixed left-1/2 top-1/2 z-50 flex w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col gap-3.5 p-5">
        <h2 className="text-base font-semibold text-ink">Edit opportunity</h2>
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-3">
          <Input label="Opportunity name" {...register('name')} />
          <Input label="Amount" type="number" step="0.01" {...register('amount', { valueAsNumber: true })} />
          <Input label="Expected close date" type="date" {...register('expectedCloseDate')} />
          <Input label="Source" placeholder="e.g. Referral, Website, Upwork" {...register('source')} />
          {statusEditable && (
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
              Status
              <select {...register('status')} className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal">
                <option value="open">Open</option>
                <option value="abandoned">Abandoned</option>
              </select>
            </label>
          )}
          <div className="mt-1 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Save
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
}
